// @flow

import type { State } from "@store/reducers";
import type {
  AbsenceReason,
  AbsenceReasonsState,
} from "@modules/service/reducer/absence-reasons/types";
import type { ApprovableContainer } from "@modules/service/types";
import { removeNullAndUndefinedProps } from "@transformers/object";
import Ajv from "ajv";
import type { PaginationExtendedType, SortOrder } from "@types";
import { getPagination } from "@selectors";
import type { Settings } from "@modules/settings/types";

export const getAbsenceReasonsActiveList = (state: State) => {
  return ((state.service.absenceReasons.activeList || {}).content || []).reduce(
    (acc, c) => (c.active ? [...acc, c.active] : acc),
    [],
  );
};

export const getAbsenceReasonsPendingList = (state: State) => {
  return ((state.service.absenceReasons.pendingList || {}).content || []).reduce(
    (acc, c) => (c.draft ? [...acc, c.draft] : acc),
    [],
  );
};

export const getAbsenceReasonsDraftList = (state: State) => {
  return ((state.service.absenceReasons.draftList || {}).content || []).reduce(
    (acc, c) => (c.draft ? [...acc, c.draft] : acc),
    [],
  );
};

export const getAbsenceReasonContainer = (state: State): ?ApprovableContainer<AbsenceReason> =>
  state.service.absenceReasons.current;

export const getAbsenceReasonDraft = (state: State): ?AbsenceReason =>
  state.service.absenceReasons.draft;

export const isAbsenceReasonDraftValid = (state: State): boolean => {
  const schema = getAbsenceReasonRevisionSchema(state);
  const draft = state.service.absenceReasons.draft;
  if (!schema || !draft) {
    return false;
  }
  return new Ajv().validate(schema, removeNullAndUndefinedProps(draft));
};

// MODALS
export const getShowAddAbsenceReasonModal = (state: State): boolean =>
  state.service.absenceReasons.modals.showAddAbsenceReasonModal;

export const getShowEditAbsenceReasonModal = (state: State): boolean =>
  state.service.absenceReasons.modals.showEditModal;

export const getAbsenceReasonsModalStep = (state: State): number =>
  state.service.absenceReasons.modals.modalStep;

export const getAbsenceReasonRevisionSchema = (state: State) => state.service.absenceReasons.schema;

export const getShowAbsenceReasonSubmitModal = (state: State): boolean =>
  state.service.absenceReasons.modals.showSubmitModal;
export const getShowAbsenceReasonCancelModal = (state: State): boolean =>
  state.service.absenceReasons.modals.showCancelModal;

export const getShowAbsenceReasonDiscardModal = (state: State): boolean =>
  state.service.absenceReasons.modals.showDiscardModal;

export const getShowAbsenceReasonCancelDiscardModal = (state: State): boolean =>
  state.service.absenceReasons.modals.showCancelDiscardModal;

export const getShowAbsenceReasonDisableModal = (state: State): boolean =>
  state.service.absenceReasons.modals.showDisableModal;

// // CONFIG

export const absenceReasonsSelector = (state: State): AbsenceReasonsState =>
  state.service.absenceReasons;

// // DATA

export const getAbsenceReasonsList = (state: State): Array<ApprovableContainer<AbsenceReason>> =>
  (absenceReasonsSelector(state).list || {}).content || [];

export const getAbsenceReasonsContainerActiveList = (
  state: State,
): Array<ApprovableContainer<AbsenceReason>> =>
  (absenceReasonsSelector(state).activeList || {}).content || [];

export const getAbsenceReasonsContainerPendingList = (
  state: State,
): Array<ApprovableContainer<AbsenceReason>> =>
  (absenceReasonsSelector(state).pendingList || {}).content || [];

export const getAbsenceReasonsContainerDraftList = (
  state: State,
): Array<ApprovableContainer<AbsenceReason>> =>
  (absenceReasonsSelector(state).draftList || {}).content || [];

export const getAbsenceReasonsPagination = (state: State): PaginationExtendedType =>
  getPagination(absenceReasonsSelector(state).list);

export const getActiveAbsenceReasonsPagination = (state: State): PaginationExtendedType =>
  getPagination(absenceReasonsSelector(state).activeList);

export const getPendingAbsenceReasonsPagination = (state: State): PaginationExtendedType =>
  getPagination(absenceReasonsSelector(state).pendingList);

export const getDraftAbsenceReasonsPagination = (state: State): PaginationExtendedType =>
  getPagination(absenceReasonsSelector(state).draftList);

export const getAbsenceReasonSchema = (state: State): ?Object =>
  absenceReasonsSelector(state).schema;

export const selectAbsenceReasonsPage = (state: State): number | null =>
  ((absenceReasonsSelector(state).list || {}).pagination || {}).pageNumber;

export const selectActiveAbsenceReasonsPage = (state: State): number | null =>
  ((absenceReasonsSelector(state).activeList || {}).pagination || {}).pageNumber;

export const selectPendingAbsenceReasonsPage = (state: State): number | null =>
  ((absenceReasonsSelector(state).pendingList || {}).pagination || {}).pageNumber;

export const selectDraftAbsenceReasonsPage = (state: State): number | null =>
  ((absenceReasonsSelector(state).draftList || {}).pagination || {}).pageNumber;

export const getAbsenceReasonsSort = (state: State): Array<SortOrder> | void =>
  (getPagination(absenceReasonsSelector(state).list).sort || {}).orders;

export const getActiveAbsenceReasonsSort = (state: State): Array<SortOrder> | void =>
  (getPagination(absenceReasonsSelector(state).activeList).sort || {}).orders;

export const getPendingAbsenceReasonsSort = (state: State): Array<SortOrder> | void =>
  (getPagination(absenceReasonsSelector(state).pendingList).sort || {}).orders;

export const getDraftAbsenceReasonsSort = (state: State): Array<SortOrder> | void =>
  (getPagination(absenceReasonsSelector(state).draftList).sort || {}).orders;

export const getIsEssentialsService = (settings: ?Settings): boolean => {
  if (!settings || !settings.clientSettings || !settings.clientSettings.settings) {
    return false;
  }
  return settings.clientSettings.settings.serviceType === "ESSENTIALS";
};

export const getAbsenceReasonsFilterState = (state: State) => {
  return state.service.absenceReasons.filterState;
};

export const getAbsenceReasonsFilterType = (state: State) => {
  return state.service.absenceReasons.filterType;
};
